import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import WelcomeOverlay from '../components/WelcomeOverlay';
import DashboardHero from '../components/DashboardHero';
import LeaseDurationCounter from '../components/LeaseDurationCounter';
import OwnerCockpit from '../components/OwnerCockpit';
import { agencySettingsAPI } from '../lib/api';
import { PortalNewsTicker } from '../components/DashboardNewsTicker';
import {
  Building2, Wallet, TrendingUp, TrendingDown, LogOut, Clock,
  CheckCircle2, XCircle, Send, FileText, Download, DollarSign,
  Home, ChevronDown, ChevronUp, CreditCard, Calendar, Users,
  Phone, Mail, FolderOpen, FileSignature, Loader2, Eye,
  AlertTriangle, Camera, X as XIcon, Wrench
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ownerApi = axios.create({ baseURL: `${API_URL}/api` });
ownerApi.interceptors.request.use(config => {
  const token = localStorage.getItem('owner_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
ownerApi.interceptors.response.use((response) => {
  try {
    const method = (response.config?.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)
        && 'serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'PURGE_API_CACHE' });
    }
  } catch (_) { /* noop */ }
  return response;
}, (error) => Promise.reject(error));

const formatCurrency = (amount, currency = 'XOF') => {
  if (amount === null || amount === undefined) return '0 FCFA';
  return `${Number(amount).toLocaleString('fr-FR')} ${currency === 'XOF' ? 'FCFA' : currency}`;
};

const STATUS_LABELS = {
  en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approuve: { label: 'Approuve', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  refuse: { label: 'Refuse', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const MONTHS = ['', 'Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [withdrawals, setWithdrawals] = useState({ withdrawals: [], requests: [] });
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [requestForm, setRequestForm] = useState({ amount: '', payment_method: 'especes', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);
  const [documents, setDocuments] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [agencySettings, setAgencySettings] = useState(null);
  // Maintenance tab (anomalies + repairs)
  const [anomalies, setAnomalies] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [loadingMaintenance, setLoadingMaintenance] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, withRes, tenRes] = await Promise.all([
        ownerApi.get('/owner/dashboard'),
        ownerApi.get('/owner/withdrawals'),
        ownerApi.get('/owner/tenants'),
      ]);
      setDashboard(dashRes.data);
      setWithdrawals(withRes.data);
      setTenants(tenRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('owner_token');
        navigate('/owner/login');
        return;
      }
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('owner_token');
    if (!token) { navigate('/owner/login'); return; }
    fetchData();
    // Load agency settings for welcome overlay
    agencySettingsAPI.getForOwner().then((res) => setAgencySettings(res.data)).catch(() => {});
  }, [fetchData, navigate]);

  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [downloadingAnnual, setDownloadingAnnual] = useState(false);
  const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());
  const [monthlyMonth, setMonthlyMonth] = useState(new Date().getMonth() + 1);
  const [downloadingMonthly, setDownloadingMonthly] = useState(false);

  const downloadAnnualStatement = async () => {
    setDownloadingAnnual(true);
    try {
      const r = await ownerApi.get('/owner/annual-statement/pdf', {
        params: { year: annualYear },
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `releve_annuel_${annualYear}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success(`Relevé ${annualYear} téléchargé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setDownloadingAnnual(false);
    }
  };

  const downloadMonthlyStatement = async () => {
    setDownloadingMonthly(true);
    try {
      const r = await ownerApi.get('/owner/monthly-statement/pdf', {
        params: { year: monthlyYear, month: monthlyMonth },
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `releve_mensuel_${monthlyYear}_${String(monthlyMonth).padStart(2, '0')}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success('Relevé mensuel téléchargé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setDownloadingMonthly(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('owner_token');
    localStorage.removeItem('owner_data');
    localStorage.removeItem('owner_properties');
    navigate('/owner/login');
  };

  const handleSubmitRequest = async () => {
    if (!selectedProperty || !requestForm.amount) {
      toast.error('Veuillez remplir le bien et le montant');
      return;
    }
    setSubmitting(true);
    try {
      await ownerApi.post('/owner/withdrawal-request', {
        property_id: selectedProperty,
        amount: parseFloat(requestForm.amount),
        payment_method: requestForm.payment_method,
        notes: requestForm.notes
      });
      toast.success('Demande de retrait soumise');
      setShowRequestModal(false);
      setRequestForm({ amount: '', payment_method: 'especes', notes: '' });
      setSelectedProperty('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async (url, filename) => {
    setDownloadingPdf(url);
    try {
      const response = await ownerApi.get(url, { responseType: 'blob' });
      // Check if response is actually a PDF (not an error JSON)
      if (response.data.type && response.data.type.includes('json')) {
        const text = await response.data.text();
        const err = JSON.parse(text);
        toast.error(err.detail || 'Erreur serveur');
        return;
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      // Try download link first
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      // Fallback: open in new tab for mobile compatibility
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
      toast.success('Document telecharge');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Erreur lors du telechargement';
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors du telechargement');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const loadDocuments = async () => {
    if (!dashboard) return;
    setLoadingDocs(true);
    try {
      const results = {};
      for (const prop of dashboard.properties) {
        const res = await ownerApi.get(`/owner/documents/${prop.id}`);
        results[prop.id] = res.data;
      }
      setDocuments(results);
    } catch {
      toast.error('Erreur chargement des documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  const loadVisits = async () => {
    setLoadingVisits(true);
    try {
      const res = await ownerApi.get('/owner/property-visits');
      setVisits(res.data);
    } catch {
      toast.error('Erreur chargement des visites');
    } finally {
      setLoadingVisits(false);
    }
  };

  const loadMaintenance = async () => {
    setLoadingMaintenance(true);
    try {
      const [a, r] = await Promise.all([
        ownerApi.get('/owner/anomalies'),
        ownerApi.get('/owner/repairs'),
      ]);
      setAnomalies(a.data.items || []);
      setRepairs(r.data.items || []);
    } catch {
      toast.error('Erreur chargement anomalies/réparations');
    } finally {
      setLoadingMaintenance(false);
    }
  };

  // Onglet ciblé par un message cliquable (?tab=...)
  const [ownerTab, setOwnerTab] = useState('properties');
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const { key: locationKey } = useLocation();
  useEffect(() => {
    if (!tabParam) return;
    setOwnerTab(tabParam);
    if (tabParam === 'documents') loadDocuments();
    if (tabParam === 'visits') loadVisits();
    if (tabParam === 'maintenance') loadMaintenance();
    setTimeout(() => document.querySelector('[data-testid="owner-tabs"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey]);


  const loadFinancialSummary = async (propertyId, year) => {
    setLoadingFinancial(true);
    try {
      const res = await ownerApi.get(`/owner/financial-summary/${propertyId}?year=${year || new Date().getFullYear()}`);
      setFinancialData(res.data);
    } catch {
      toast.error('Erreur de chargement du releve');
    } finally {
      setLoadingFinancial(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-4 border-[#1B3A4B] border-t-transparent rounded-full" /></div>;
  if (!dashboard) return null;

  const { owner, summary, properties } = dashboard;
  const activeTenants = tenants.filter(t => t.lease_status === 'actif');
  const allDocs = Object.values(documents).flat();

  return (
    <div className="min-h-screen bg-hero-gradient" data-testid="owner-dashboard">
      <WelcomeOverlay
        spaceType="owner"
        userId={owner?.id || owner?.client_id}
        userName={owner?.name}
        agencyName={agencySettings?.name}
        agencyLogoUrl={agencySettings?.logo_url}
        agencySlogan={agencySettings?.slogan}
      />
      <PortalNewsTicker portal="owner" />
      {/* Header — Élégance Africaine */}
      <div className="bg-gradient-to-r from-[#2B3A2C] via-[#3E4F3F] to-[#2B3A2C] text-white px-4 py-5 shadow-lg border-b-4 border-[#B85C38] sticky top-0 z-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 wax-pattern pointer-events-none" />
        <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-3 relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#B85C38] to-[#D07A55] flex items-center justify-center shadow-md ring-2 ring-white/20">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg tracking-tight">{owner.name}</h1>
              <p className="text-[#F5EFE1] text-xs font-medium tracking-wide uppercase">Espace Propriétaire</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Relevé mensuel */}
            <div className="flex items-center gap-1 bg-white/10 border border-white/20 rounded-lg px-2 py-1">
              <select
                value={monthlyMonth}
                onChange={(e) => setMonthlyMonth(parseInt(e.target.value, 10))}
                className="bg-transparent text-white text-xs font-semibold focus:outline-none pr-1"
                data-testid="monthly-month-select"
              >
                {['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'].map((m, i) => (
                  <option key={i} value={i+1} className="text-[#2B3A2C]">{m}</option>
                ))}
              </select>
              <select
                value={monthlyYear}
                onChange={(e) => setMonthlyYear(parseInt(e.target.value, 10))}
                className="bg-transparent text-white text-xs font-semibold focus:outline-none"
                data-testid="monthly-year-select"
              >
                {[0, 1, 2].map((offset) => {
                  const y = new Date().getFullYear() - offset;
                  return <option key={y} value={y} className="text-[#2B3A2C]">{y}</option>;
                })}
              </select>
              <Button
                onClick={downloadMonthlyStatement}
                disabled={downloadingMonthly}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/10 h-7 px-2 text-xs"
                data-testid="download-monthly-statement"
                title="Relevé mensuel PDF"
              >
                {downloadingMonthly ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              </Button>
            </div>

            {/* Relevé annuel */}
            <select
              value={annualYear}
              onChange={(e) => setAnnualYear(parseInt(e.target.value, 10))}
              className="bg-white/10 border border-white/20 text-white rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#B85C38]"
              data-testid="annual-year-select"
            >
              {[0, 1, 2].map((offset) => {
                const y = new Date().getFullYear() - offset;
                return <option key={y} value={y} className="text-[#2B3A2C]">{y}</option>;
              })}
            </select>
            <Button
              onClick={downloadAnnualStatement}
              disabled={downloadingAnnual}
              size="sm"
              className="bg-[#B85C38] hover:bg-[#8F4426] text-white shadow-md"
              data-testid="download-annual-statement"
            >
              {downloadingAnnual
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Download className="w-4 h-4 mr-1" />}
              Relevé annuel
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/90 hover:text-white hover:bg-white/10" data-testid="owner-logout">
              <LogOut className="w-4 h-4 mr-1" /> Déconnexion
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-5 animate-fade-up">
        {/* Compute personalized greeting name : use property owner_name(s) if available,
            otherwise fall back to login client name. */}
        {(() => null)()}
        {/* === Cockpit === */}
        <OwnerCockpit cockpit={dashboard?.cockpit} />
        {/* === Hero === */}
        <DashboardHero
          accentColor="#D4A745"
          role="Proprietaire"
          greeting={(() => {
            const uniqueOwners = Array.from(
              new Set((properties || []).map(p => p.owner_name).filter(Boolean))
            );
            let displayName;
            if (uniqueOwners.length === 1) {
              displayName = uniqueOwners[0];
            } else if (uniqueOwners.length > 1) {
              displayName = `${uniqueOwners[0]} +${uniqueOwners.length - 1}`;
            } else {
              displayName = (owner?.name || '').split(' ')[0] || 'Bienvenue';
            }
            return `Bonjour, ${displayName}`;
          })()}
          subtitle="Suivez en temps reel vos biens, soldes et retraits."
          metrics={[
            { label: 'Biens', value: summary.total_properties, icon: Home },
            { label: summary.total_balance < 0 ? 'Decouvert' : 'Solde total', value: formatCurrency(summary.total_balance), icon: Wallet, accent: true },
            { label: 'Total percu', value: formatCurrency(summary.total_received), icon: TrendingUp },
            { label: 'Locataires', value: activeTenants.length, icon: Users },
          ]}
        />

        {/* Request Withdrawal Button */}
        <Button onClick={() => setShowRequestModal(true)} className="w-full btn-brand h-12 text-base rounded-xl" data-testid="request-withdrawal-btn">
          <Send className="w-5 h-5 mr-2" /> Demander un retrait
        </Button>

        {/* Tabs */}
        <Tabs value={ownerTab} data-testid="owner-tabs" onValueChange={(v) => {
          setOwnerTab(v);
          if (v === 'documents' && allDocs.length === 0) loadDocuments();
          if (v === 'visits' && visits.length === 0) loadVisits();
          if (v === 'maintenance' && anomalies.length === 0 && repairs.length === 0) loadMaintenance();
        }}>
          <TabsList className="flex flex-wrap h-auto gap-1.5 p-1.5 bg-white border border-gray-200 shadow-sm rounded-xl w-full">
            <TabsTrigger value="properties" data-testid="tab-properties" className="flex-1 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">Biens</TabsTrigger>
            <TabsTrigger value="tenants" data-testid="tab-tenants" className="flex-1 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">Locataires</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="flex-1 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">Documents</TabsTrigger>
            <TabsTrigger value="visits" data-testid="tab-visits" className="flex-1 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">Visites</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance" className="flex-1 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <span className="flex items-center gap-1.5">
                <span>Entretien</span>
                {((dashboard?.cockpit?.anomalies_count || 0) + (dashboard?.cockpit?.repairs_count || 0)) > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white">
                    {(dashboard?.cockpit?.anomalies_count || 0) + (dashboard?.cockpit?.repairs_count || 0)}
                  </span>
                )}
              </span>
            </TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="tab-withdrawals" className="flex-1 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">Retraits</TabsTrigger>
            <TabsTrigger value="financial" data-testid="tab-financial" className="flex-1 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">Releves</TabsTrigger>
          </TabsList>

          {/* ====== Properties Tab ====== */}
          <TabsContent value="properties" className="space-y-3 mt-3">
            {properties.map(prop => (
              <Card key={prop.id} data-testid={`property-card-${prop.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedProperty(expandedProperty === prop.id ? null : prop.id)}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#1B3A4B]/10 flex items-center justify-center">
                        <Home className="w-5 h-5 text-[#1B3A4B]" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{prop.title}</p>
                        <p className="text-xs text-gray-500">{prop.address} {prop.district}</p>
                        {prop.owner_name && (
                          <p className="text-xs text-[#1B3A4B] font-semibold mt-0.5 inline-flex items-center gap-1" data-testid={`owner-name-${prop.id}`}>
                            <Users className="w-3 h-3" />
                            Propriétaire&nbsp;: {prop.owner_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className={`font-bold ${prop.available_balance < 0 ? 'text-red-600' : 'text-[#1B3A4B]'}`}>{formatCurrency(prop.available_balance)}</p>
                        <p className="text-xs text-gray-500">Solde</p>
                      </div>
                      {expandedProperty === prop.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                  {expandedProperty === prop.id && (
                    <div className="mt-4 pt-4 border-t space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs">Loyer mensuel</p>
                          <p className="font-semibold">{formatCurrency(prop.monthly_rent)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs">Commission agence</p>
                          <p className="font-semibold">{prop.commission_rate}%</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs">Total percu</p>
                          <p className="font-semibold text-green-700">{formatCurrency(prop.total_received)}</p>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs">Total retire</p>
                          <p className="font-semibold text-amber-700">{formatCurrency(prop.total_withdrawn)}</p>
                        </div>
                      </div>
                      {prop.pending_requests > 0 && (
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                          <Clock className="w-4 h-4 text-amber-600" />
                          <span className="text-xs text-amber-700">{prop.pending_requests} demande(s) en attente ({formatCurrency(prop.pending_amount)})</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{prop.active_tenants} locataire(s) actif(s)</Badge>
                        <Badge className={`text-xs ${prop.status === 'loue' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{prop.status}</Badge>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* ====== Tenants Tab ====== */}
          <TabsContent value="tenants" className="space-y-3 mt-3">
            {tenants.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Aucun locataire</p>
              </div>
            ) : (
              tenants.map((tenant, idx) => (
                <Card key={tenant.id + '-' + idx} data-testid={`tenant-card-${idx}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{tenant.name}</p>
                          <p className="text-xs text-gray-500">{tenant.property_title}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#1B3A4B]">{formatCurrency(tenant.monthly_rent, tenant.currency)}/mois</p>
                        <Badge className={`text-xs ${tenant.lease_status === 'actif' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {tenant.lease_status === 'actif' ? 'Actif' : tenant.lease_status}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 text-sm text-gray-600">
                      {tenant.phone && (
                        <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" />{tenant.phone}</span>
                      )}
                      {tenant.email && (
                        <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gray-400" />{tenant.email}</span>
                      )}
                      <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-400" />Code: {tenant.client_code}</span>
                    </div>
                    {tenant.start_date && (
                      <div className="mt-3">
                        <LeaseDurationCounter
                          startDate={tenant.start_date}
                          endDate={tenant.lease_status === 'termine' ? tenant.end_date : null}
                          accentColor={tenant.lease_status === 'termine' ? 'rose' : 'emerald'}
                          label={`Ancienneté du bail — ${tenant.name.split(' ')[0]}`}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="documents" className="space-y-3 mt-3">
            {loadingDocs ? (
              <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>
            ) : allDocs.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Aucun document disponible</p>
                <p className="text-xs mt-1">Cliquez sur l'onglet pour charger vos documents</p>
              </div>
            ) : (
              properties.map(prop => {
                const propDocs = documents[prop.id] || [];
                if (propDocs.length === 0) return null;
                return (
                  <Card key={prop.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Home className="w-4 h-4 text-[#1B3A4B]" />
                        {prop.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {propDocs.map((doc, idx) => (
                        <div key={`${doc.type}-${doc.id || doc.reference || idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`doc-${doc.type}-${idx}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${doc.type === 'mandate' ? 'bg-[#1B3A4B]/10' : 'bg-blue-100'}`}>
                              {doc.type === 'mandate'
                                ? <FileSignature className="w-4 h-4 text-[#1B3A4B]" />
                                : <FileText className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-900">{doc.label}</p>
                              <p className="text-xs text-gray-500">{doc.type === 'mandate' ? 'Mandat de courtage' : 'Avis d\'entree en location'}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadPdf(doc.url, `${doc.type === 'mandate' ? 'mandat_courtage' : 'avis_entree'}_${prop.title.replace(/\s+/g, '_')}.pdf`)}
                            disabled={downloadingPdf === doc.url}
                            className="text-[#1B3A4B] border-[#1B3A4B] hover:bg-[#1B3A4B] hover:text-white"
                            data-testid={`download-doc-${doc.type}-${idx}`}
                          >
                            {downloadingPdf === doc.url
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <><Download className="w-4 h-4 mr-1" /> PDF</>}
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* ====== Visits Tab ====== */}
          <TabsContent value="visits" className="space-y-3 mt-3">
            {loadingVisits ? (
              <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>
            ) : visits.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Aucune visite enregistree</p>
              </div>
            ) : (
              visits.map((visit) => (
                <Card key={visit.id} data-testid={`visit-card-${visit.id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-[#1B3A4B]" />
                        {visit.property_title} - {visit.reference}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={visit.status === 'terminee' ? 'bg-green-100 text-green-700' : visit.status === 'en_cours' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                          {visit.status === 'terminee' ? 'Terminee' : visit.status === 'en_cours' ? 'En cours' : 'Planifiee'}
                        </Badge>
                        <span className="text-xs text-gray-400">{visit.visit_date}</span>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500 mb-2">Inspecteur: {visit.inspector_name}</p>
                    {visit.notes && <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded mb-2">{visit.notes}</p>}

                    {visit.anomalies && visit.anomalies.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-700">{visit.anomalies.length} anomalie(s) :</p>
                        {visit.anomalies.map((anomaly, aIdx) => (
                          <div key={anomaly.id || aIdx} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-start gap-2">
                              <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${anomaly.priority === 'urgente' ? 'bg-red-100' : anomaly.priority === 'faible' ? 'bg-green-100' : 'bg-amber-100'}`}>
                                <Wrench className={`w-3.5 h-3.5 ${anomaly.priority === 'urgente' ? 'text-red-600' : anomaly.priority === 'faible' ? 'text-green-600' : 'text-amber-600'}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium">{anomaly.category}</span>
                                  <Badge className={`text-xs ${anomaly.status === 'resolu' ? 'bg-green-100 text-green-700' : anomaly.status === 'en_cours' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                    {anomaly.status === 'resolu' ? 'Resolu' : anomaly.status === 'en_cours' ? 'En cours' : 'Signale'}
                                  </Badge>
                                  <Badge className={`text-xs ${anomaly.priority === 'urgente' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                    {anomaly.priority}
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{anomaly.description}</p>
                                {anomaly.tenant_name && <p className="text-xs text-gray-400">Locataire: {anomaly.tenant_name}</p>}

                                {/* Anomaly photos */}
                                {anomaly.photos && anomaly.photos.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {anomaly.photos.map((photo, pIdx) => (
                                      <div key={`${anomaly.id || anomaly._id || 'a'}-${photo || pIdx}`} className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => setPhotoPreview(photo)}>
                                        <img src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} alt={`Photo ${pIdx + 1}`}
                                          className="w-full h-full object-cover" />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">Aucune anomalie signalee</p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* ====== Maintenance Tab (Anomalies + Repairs) ====== */}
          <TabsContent value="maintenance" className="space-y-4 mt-3" data-testid="maintenance-tab-content">
            {loadingMaintenance ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-4 border-[#B85C38] border-t-transparent rounded-full" />
              </div>
            ) : (
              <>
                {/* --- Anomalies --- */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-[#2B3A2C] text-sm uppercase tracking-wide">Anomalies signalées</h3>
                    <span className="text-xs text-gray-500">{anomalies.length} au total</span>
                  </div>
                  {anomalies.length === 0 ? (
                    <Card>
                      <CardContent className="p-6 text-center text-sm text-gray-500">
                        Aucune anomalie signalée sur vos biens pour le moment.
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {anomalies.map((a) => {
                        const sev = (a.severity || 'normale').toLowerCase();
                        const sevColor = sev === 'urgente' ? 'bg-red-100 text-red-700 border-red-200'
                          : sev === 'elevee' || sev === 'elevée' ? 'bg-orange-100 text-orange-700 border-orange-200'
                          : 'bg-amber-100 text-amber-700 border-amber-200';
                        const statusMap = { ouverte: ['bg-red-500', 'À traiter'], en_cours: ['bg-blue-500', 'En cours'], resolu: ['bg-green-500', 'Résolue'], annule: ['bg-gray-400', 'Annulée'] };
                        const [stDot, stLabel] = statusMap[a.status] || ['bg-gray-400', a.status];
                        return (
                          <Card key={a.id} data-testid={`anomaly-card-${a.id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`w-2 h-2 rounded-full ${stDot}`} />
                                    <h4 className="font-semibold text-sm text-[#2B3A2C] truncate">{a.title || a.category || 'Anomalie'}</h4>
                                  </div>
                                  <p className="text-xs text-gray-600 line-clamp-2">{a.description || '—'}</p>
                                </div>
                                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${sevColor}`}>
                                  {sev}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 mt-2">
                                <span>📍 {a.property_title}</span>
                                <span>📅 {a.visit_date}</span>
                                <span>👤 {a.reported_by}</span>
                                <span className={`font-semibold ${a.status === 'ouverte' ? 'text-red-600' : 'text-gray-600'}`}>· {stLabel}</span>
                              </div>
                              {a.photos && a.photos.length > 0 && (
                                <div className="flex gap-1.5 mt-2 overflow-x-auto">
                                  {a.photos.slice(0, 4).map((ph, i) => (
                                    <img
                                      key={i}
                                      src={ph.url || ph}
                                      alt=""
                                      className="w-14 h-14 object-cover rounded-md cursor-pointer flex-shrink-0 border"
                                      onClick={() => setPhotoPreview(ph.url || ph)}
                                    />
                                  ))}
                                </div>
                              )}
                              {a.resolution_notes && (
                                <div className="mt-2 p-2 rounded bg-green-50 border border-green-200 text-xs text-green-800">
                                  <b>Résolution :</b> {a.resolution_notes}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* --- Repairs --- */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-[#2B3A2C] text-sm uppercase tracking-wide">Réparations</h3>
                    <span className="text-xs text-gray-500">{repairs.length} au total</span>
                  </div>
                  {repairs.length === 0 ? (
                    <Card>
                      <CardContent className="p-6 text-center text-sm text-gray-500">
                        Aucune réparation enregistrée sur vos biens.
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {repairs.map((r) => {
                        const statusMap = {
                          planifiee: ['bg-blue-100 text-blue-700', 'Planifiée'],
                          en_cours: ['bg-amber-100 text-amber-700', 'En cours'],
                          terminee: ['bg-green-100 text-green-700', 'Terminée'],
                          annulee: ['bg-gray-100 text-gray-500', 'Annulée'],
                        };
                        const [badge, label] = statusMap[r.status] || ['bg-gray-100 text-gray-600', r.status];
                        return (
                          <Card key={r.id} data-testid={`repair-card-${r.id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-semibold text-sm text-[#2B3A2C]">{r.reference}</h4>
                                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${badge}`}>{label}</span>
                                  </div>
                                  <p className="text-xs text-gray-600 capitalize">{r.repair_type} — {r.description}</p>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-bold text-[#B85C38]">
                                    {new Intl.NumberFormat('fr-FR').format(r.total_cost || 0)} XOF
                                  </div>
                                  <div className="text-[10px] text-gray-500">
                                    {r.paid_by === 'owner' ? 'À votre charge' : r.paid_by === 'tenant' ? 'À charge locataire' : 'Agence'}
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 mt-2">
                                <span>📍 {r.property_title}</span>
                                {r.provider_name && <span>🔧 {r.provider_name}</span>}
                                {r.scheduled_date && <span>📅 {new Date(r.scheduled_date).toLocaleDateString('fr-FR')}</span>}
                                {r.completion_date && <span>✓ {new Date(r.completion_date).toLocaleDateString('fr-FR')}</span>}
                              </div>
                              {r.invoices && r.invoices.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {r.invoices.map((inv) => (
                                    <a
                                      key={inv.id}
                                      href={`${process.env.REACT_APP_BACKEND_URL}${inv.file_url}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline bg-blue-50 px-2 py-1 rounded border border-blue-200"
                                      data-testid={`repair-invoice-${inv.id}`}
                                    >
                                      📄 {inv.label}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* ====== Withdrawals Tab ====== */}
          <TabsContent value="withdrawals" className="space-y-3 mt-3">
            <h3 className="font-medium text-gray-700 text-sm">Demandes en cours</h3>
            {withdrawals.requests.filter(r => r.status === 'en_attente').length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Aucune demande en cours</p>
            ) : withdrawals.requests.filter(r => r.status === 'en_attente').map(req => {
              const st = STATUS_LABELS[req.status] || STATUS_LABELS.en_attente;
              return (
                <Card key={req.id} className="border-amber-200">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{formatCurrency(req.amount)}</span>
                        <Badge className={st.color + ' text-xs'}>{st.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{req.property_title} - {req.reference}</p>
                      <p className="text-xs text-gray-400">{new Date(req.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <Clock className="w-5 h-5 text-amber-500" />
                  </CardContent>
                </Card>
              );
            })}

            <h3 className="font-medium text-gray-700 text-sm mt-4">Historique</h3>
            {[...withdrawals.requests.filter(r => r.status !== 'en_attente'), ...withdrawals.withdrawals].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 20).map((item, idx) => {
              const isRequest = !!item.reference?.startsWith('DEM-');
              const isCompleted = !isRequest || item.status === 'approuve';
              const st = isRequest ? STATUS_LABELS[item.status] : STATUS_LABELS.approuve;
              const withdrawalId = isRequest ? item.withdrawal_id : item.id;
              return (
                <Card key={item.id || idx}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{formatCurrency(item.amount)}</span>
                        <Badge className={st.color + ' text-xs'}>{isRequest ? st.label : 'Effectue'}</Badge>
                      </div>
                      <p className="text-xs text-gray-500">{item.property_title} - {item.reference}</p>
                      <p className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.status === 'refuse' && item.admin_response && (
                        <p className="text-xs text-red-500 max-w-[150px] text-right">{item.admin_response}</p>
                      )}
                      {isCompleted && withdrawalId && (
                        <Button variant="outline" size="sm"
                          onClick={() => downloadPdf(`/owner/withdrawals/${withdrawalId}/pdf`, `attestation_retrait_${item.reference || withdrawalId}.pdf`)}
                          disabled={downloadingPdf === `/owner/withdrawals/${withdrawalId}/pdf`}
                          className="text-[#1B3A4B] border-[#1B3A4B] hover:bg-[#1B3A4B] hover:text-white"
                          data-testid={`download-withdrawal-pdf-${idx}`}
                        >
                          {downloadingPdf === `/owner/withdrawals/${withdrawalId}/pdf`
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <><Download className="w-4 h-4 mr-1" /> Attestation</>}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {withdrawals.withdrawals.length === 0 && withdrawals.requests.filter(r => r.status !== 'en_attente').length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">Aucun historique</p>
            )}
          </TabsContent>

          {/* ====== Financial Tab ====== */}
          <TabsContent value="financial" className="space-y-3 mt-3">
            <div className="flex items-center gap-3">
              <Select onValueChange={(v) => {
                const [pid, yr] = v.split('|');
                setSelectedProperty(pid);
                loadFinancialSummary(pid, yr);
              }}>
                <SelectTrigger data-testid="financial-select"><SelectValue placeholder="Selectionnez un bien et une annee" /></SelectTrigger>
                <SelectContent>
                  {properties.map(p => [new Date().getFullYear(), new Date().getFullYear() - 1].map(yr => (
                    <SelectItem key={`${p.id}-${yr}`} value={`${p.id}|${yr}`}>{p.title} - {yr}</SelectItem>
                  )))}
                </SelectContent>
              </Select>
            </div>

            {loadingFinancial && <p className="text-center text-gray-400 py-8">Chargement...</p>}

            {financialData && !loadingFinancial && (
              <Card data-testid="financial-summary">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#1B3A4B]" />
                    Releve {financialData.year} - {financialData.property.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Loyers percus</p>
                      <p className="font-bold text-green-700">{formatCurrency(financialData.summary.total_received)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Commission ({financialData.summary.commission_rate}%)</p>
                      <p className="font-bold text-gray-700">-{formatCurrency(financialData.summary.total_commission)}</p>
                    </div>
                    <div className="bg-[#1B3A4B]/5 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Net proprietaire</p>
                      <p className="font-bold text-[#1B3A4B]">{formatCurrency(financialData.summary.net_owner)}</p>
                    </div>
                    <div className={`rounded-lg p-3 ${financialData.summary.balance < 0 ? 'bg-red-50' : 'bg-amber-50'}`}>
                      <p className="text-xs text-gray-500">Solde restant</p>
                      <p className={`font-bold ${financialData.summary.balance < 0 ? 'text-red-600' : 'text-amber-700'}`}>{formatCurrency(financialData.summary.balance)}</p>
                    </div>
                  </div>

                  {financialData.monthly_breakdown.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Detail mensuel</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-2 text-xs font-medium text-gray-500">Mois</th>
                              <th className="text-right p-2 text-xs font-medium text-gray-500">Percu</th>
                              <th className="text-right p-2 text-xs font-medium text-gray-500">Commission</th>
                              <th className="text-right p-2 text-xs font-medium text-gray-500">Net</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financialData.monthly_breakdown.map(m => (
                              <tr key={m.month} className="border-t">
                                <td className="p-2 text-gray-700">{MONTHS[m.month] || m.month}</td>
                                <td className="p-2 text-right text-green-700">{formatCurrency(m.received)}</td>
                                <td className="p-2 text-right text-gray-500">-{formatCurrency(m.commission)}</td>
                                <td className="p-2 text-right font-medium">{formatCurrency(m.net)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Photo Preview */}
      {photoPreview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPhotoPreview(null)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button onClick={() => setPhotoPreview(null)} className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg z-10">
              <XIcon className="w-5 h-5" />
            </button>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${photoPreview}`} alt="Anomalie"
              className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          </div>
        </div>
      )}

      {/* Withdrawal Request Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Demande de retrait</DialogTitle>
            <DialogDescription>Votre demande sera soumise a validation par l'agence</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Bien concerne</Label>
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger data-testid="select-property"><SelectValue placeholder="Selectionnez un bien" /></SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.title} (Solde: {formatCurrency(p.available_balance)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (FCFA)</Label>
              <Input type="number" value={requestForm.amount} onChange={e => setRequestForm(f => ({ ...f, amount: e.target.value }))} placeholder="Ex: 500000" data-testid="request-amount" />
            </div>
            <div>
              <Label>Mode de paiement</Label>
              <Select value={requestForm.payment_method} onValueChange={v => setRequestForm(f => ({ ...f, payment_method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="especes">Especes</SelectItem>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="orange_money">Orange Money</SelectItem>
                  <SelectItem value="wave">Wave</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea value={requestForm.notes} onChange={e => setRequestForm(f => ({ ...f, notes: e.target.value }))} placeholder="Details supplementaires..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>Annuler</Button>
            <Button onClick={handleSubmitRequest} disabled={submitting} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="submit-request">
              {submitting ? 'Envoi...' : 'Soumettre la demande'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwnerDashboard;
